/**
 * @file BackdropDecorGroup.tsx
 * @description Группа «Рисунки» вкладки «Фон и рамка» (phase19.16 Р1; SB11,
 * SB89; uiux-design-r1 §1; SB122 — группа-карточка): выключены или до трёх
 * своих рисунков из медиатеки. Включены, а рисунков нет — строка-подсказка
 * (пустая группа не прячется, uiux-settings-groups §6).
 *
 * SB120 этап 2 (uiux-sb120-stage2 §2): рисунок — точка x/y по окну, ширина
 * ПК / планшет / телефон и яркость (`BackdropImageRow`); «мягкое свечение»
 * ушло в свою группу «Свечение» (`HazeGroup`) — сила и цвет числом.
 */

import { SITE_LIMITS, type SiteBackdropImage, type SiteTheme } from "@chaiberi/shared";
import { Plus } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { MediaPickerDialog } from "../media/MediaPickerDialog";
import { Segmented } from "../Segmented";
import { BackdropImageRow } from "./BackdropImageRow";
import { decorSummary } from "../../../lib/site-view/backdrop-summary";
import { backdropTooWideOnPhone, newBackdropImage } from "../../../lib/site-view/backdrop-images";

type Backdrop = SiteTheme["backdrop"];

const MODES: ReadonlyArray<{ value: Backdrop["mode"]; label: string }> = [
  { value: "off", label: "Без рисунков" },
  { value: "images", label: "Свои рисунки из медиатеки" },
];

interface Props {
  backdrop: Backdrop;
  onBackdrop: (patch: Partial<Backdrop>) => void;
}

export function BackdropDecorGroup({ backdrop, onBackdrop }: Props) {
  const [picking, setPicking] = useState(false);
  const full = backdrop.images.length >= SITE_LIMITS.backdropImages;

  function updateImage(index: number, next: SiteBackdropImage | null) {
    const images = backdrop.images
      .map((img, i) => (i === index ? next : img))
      .filter((img): img is SiteBackdropImage => img !== null);
    onBackdrop({ images });
  }

  return (
    <SettingsGroup
      id="decor"
      title="Рисунки"
      hint="Украшения по краям и углам окна под всеми блоками. На телефоне можно скрыть."
      summary={decorSummary(backdrop)}
      // «!» — рисунок закроет текст на телефоне (то же правило, что предупреждение в строке рисунка).
      status={backdrop.mode === "images" && backdrop.images.some(backdropTooWideOnPhone) ? "warning" : undefined}
    >
      <Segmented aria-label="Рисунки на фоне страницы" options={MODES} value={backdrop.mode} onChange={(mode) => onBackdrop({ mode })} />
      {backdrop.mode === "images" ? (
        <div className="flex flex-col gap-2">
          {backdrop.images.length === 0 ? (
            <FieldHint className="mt-0">Рисунков пока нет — фон страницы без украшений. Добавьте до {SITE_LIMITS.backdropImages} из медиатеки.</FieldHint>
          ) : null}
          {backdrop.images.map((image, index) => (
            <BackdropImageRow key={`${image.mediaId}:${index}`} image={image} index={index} onChange={(next) => updateImage(index, next)} />
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" disabled={full} onClick={() => setPicking(true)}>
              <Plus aria-hidden />
              Рисунок из медиатеки
            </Button>
            <span className="type-caption text-on-surface-variant">до {SITE_LIMITS.backdropImages}</span>
          </div>
          <FieldHint className="mt-0">
            Подходят PNG или WebP с прозрачным фоном. Рисунки видны на фоне страницы; под блоками со своим фоном их не видно.
          </FieldHint>
        </div>
      ) : null}
      <MediaPickerDialog
        open={picking}
        title="Рисунок на фон страницы"
        hint="PNG или WebP с прозрачным фоном."
        accept={["image/png", "image/webp"]}
        onClose={() => setPicking(false)}
        onSelect={(media) => {
          setPicking(false);
          onBackdrop({ images: [...backdrop.images, newBackdropImage(media.id, "tr")] });
        }}
      />
    </SettingsGroup>
  );
}
